import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  User,
  Building2,
  Phone,
  Mail,
  MapPin,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  Save,
  FileText,
  Shield,
  Calendar,
  Trash2,
  Plus,
  Package,
  Truck,
  DollarSign,
  AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SupplierSettingsModal
 * Settings/Profile management for suppliers
 */
export default function SupplierSettingsModal({ open, onClose, token }) {
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Profile state
  const [profile, setProfile] = useState({
    name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zip_code: "",
    website: "",
    categories: [],
    product_types: [],
    minimum_order_value: 0,
    delivery_time_days: 0,
    notes: ""
  });
  
  // Credentials state
  const [credentials, setCredentials] = useState([]);
  const [showAddCredential, setShowAddCredential] = useState(false);
  const [newCredential, setNewCredential] = useState({
    credential_type: "certification",
    credential_name: "",
    expiry_date: "",
    notes: ""
  });
  
  // Password state
  const [passwords, setPasswords] = useState({
    current: "",
    new: "",
    confirm: ""
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (open && token) {
      fetchProfile();
    }
  }, [open, token]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/supplier/profile/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error("Failed to fetch profile");
      const data = await res.json();
      setProfile(data.profile || {});
      setCredentials(data.profile?.credentials || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/supplier/profile/me`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: profile.name,
          contact_name: profile.contact_name,
          phone: profile.phone,
          address: profile.address,
          city: profile.city,
          state: profile.state,
          zip_code: profile.zip_code,
          website: profile.website,
          minimum_order_value: profile.minimum_order_value,
          delivery_time_days: profile.delivery_time_days,
          notes: profile.notes
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Failed to save");
      }
      
      toast.success("Profile updated successfully");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddCredential = async () => {
    if (!newCredential.credential_name) {
      toast.error("Credential name is required");
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/supplier/profile/credentials`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newCredential)
      });
      
      if (!res.ok) throw new Error("Failed to add credential");
      
      const data = await res.json();
      setCredentials([...credentials, data.credential]);
      setShowAddCredential(false);
      setNewCredential({ credential_type: "certification", credential_name: "", expiry_date: "", notes: "" });
      toast.success("Credential added");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCredential = async (credentialId) => {
    if (!confirm("Delete this credential?")) return;
    
    try {
      const res = await fetch(`${API_URL}/api/supplier/profile/credentials/${credentialId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Failed to delete");
      
      setCredentials(credentials.filter(c => c.id !== credentialId));
      toast.success("Credential deleted");
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleChangePassword = async () => {
    if (passwords.new !== passwords.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (passwords.new.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      const res = await fetch(`${API_URL}/api/supplier/profile/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwords.current,
          new_password: passwords.new
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Failed to change password");
      }
      
      toast.success("Password changed successfully");
      setPasswords({ current: "", new: "", confirm: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setChangingPassword(false);
    }
  };

  const getCredentialTypeLabel = (type) => {
    const types = {
      certification: "Certification",
      license: "License",
      insurance: "Insurance",
      iso: "ISO Certification",
      fda: "FDA Approval",
      other: "Other"
    };
    return types[type] || type;
  };

  const getCredentialStatusColor = (status) => {
    switch (status) {
      case "approved": return "text-green-400 bg-green-500/20";
      case "pending_review": return "text-amber-400 bg-amber-500/20";
      case "expired": return "text-red-400 bg-red-500/20";
      default: return "text-slate-400 bg-slate-500/20";
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <User className="w-5 h-5 text-cyan-400" />
            Settings & Profile
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Manage your account information, credentials, and security settings
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid grid-cols-3 w-full bg-slate-800">
            <TabsTrigger value="profile" className="flex items-center gap-2 data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
              <Building2 className="w-4 h-4" />
              <span className="hidden sm:inline">Profile</span>
            </TabsTrigger>
            <TabsTrigger value="credentials" className="flex items-center gap-2 data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
              <Shield className="w-4 h-4" />
              <span className="hidden sm:inline">Credentials</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2 data-[state=active]:bg-cyan-500/20 data-[state=active]:text-cyan-400">
              <Lock className="w-4 h-4" />
              <span className="hidden sm:inline">Security</span>
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-4 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Company Name</Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={profile.name || ""}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                    className="pl-10 bg-slate-800 border-slate-600 text-white"
                    placeholder="Your Company LLC"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Contact Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={profile.contact_name || ""}
                    onChange={(e) => setProfile({ ...profile, contact_name: e.target.value })}
                    className="pl-10 bg-slate-800 border-slate-600 text-white"
                    placeholder="John Doe"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Email (Read-only)</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={profile.email || ""}
                    className="pl-10 bg-slate-700 border-slate-600 text-slate-400"
                    disabled
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Phone</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={profile.phone || ""}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    className="pl-10 bg-slate-800 border-slate-600 text-white"
                    placeholder="555-123-4567"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-300">Address</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={profile.address || ""}
                    onChange={(e) => setProfile({ ...profile, address: e.target.value })}
                    className="pl-10 bg-slate-800 border-slate-600 text-white"
                    placeholder="123 Main St"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">City</Label>
                <Input
                  value={profile.city || ""}
                  onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                  className="bg-slate-800 border-slate-600 text-white"
                  placeholder="Houston"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-slate-300">State</Label>
                  <Input
                    value={profile.state || ""}
                    onChange={(e) => setProfile({ ...profile, state: e.target.value })}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="TX"
                    maxLength={2}
                  />
                </div>
                <div>
                  <Label className="text-slate-300">ZIP</Label>
                  <Input
                    value={profile.zip_code || ""}
                    onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })}
                    className="bg-slate-800 border-slate-600 text-white"
                    placeholder="77001"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Website</Label>
                <div className="relative mt-1">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={profile.website || ""}
                    onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                    className="pl-10 bg-slate-800 border-slate-600 text-white"
                    placeholder="https://yourcompany.com"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Minimum Order Value ($)</Label>
                <div className="relative mt-1">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type="number"
                    value={profile.minimum_order_value || 0}
                    onChange={(e) => setProfile({ ...profile, minimum_order_value: parseFloat(e.target.value) || 0 })}
                    className="pl-10 bg-slate-800 border-slate-600 text-white"
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <Label className="text-slate-300">Typical Delivery Time (Days)</Label>
                <div className="relative mt-1">
                  <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    type="number"
                    value={profile.delivery_time_days || 0}
                    onChange={(e) => setProfile({ ...profile, delivery_time_days: parseInt(e.target.value) || 0 })}
                    className="pl-10 bg-slate-800 border-slate-600 text-white"
                    placeholder="3"
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <Label className="text-slate-300">Notes</Label>
                <Textarea
                  value={profile.notes || ""}
                  onChange={(e) => setProfile({ ...profile, notes: e.target.value })}
                  className="bg-slate-800 border-slate-600 text-white"
                  placeholder="Additional information..."
                  rows={3}
                />
              </div>
            </div>
            
            <Button onClick={handleSaveProfile} disabled={saving} className="w-full bg-cyan-600 hover:bg-cyan-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Profile
            </Button>
          </TabsContent>

          {/* Credentials Tab */}
          <TabsContent value="credentials" className="space-y-4 mt-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-400">Manage your certifications and compliance documents</p>
              <Button size="sm" onClick={() => setShowAddCredential(true)} className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
            
            {credentials.length === 0 ? (
              <div className="text-center py-8 bg-slate-800/50 rounded-lg border border-dashed border-slate-600">
                <Shield className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No credentials uploaded yet</p>
                <p className="text-sm text-slate-500">Add your certifications and compliance documents</p>
              </div>
            ) : (
              <div className="space-y-3">
                {credentials.map((cred) => (
                  <div key={cred.id} className="flex items-center justify-between p-4 bg-slate-800/50 border border-slate-700 rounded-lg">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-cyan-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{cred.name}</p>
                        <p className="text-sm text-slate-400">{getCredentialTypeLabel(cred.type)}</p>
                        {cred.expiry_date && (
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            Expires: {cred.expiry_date}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded-full ${getCredentialStatusColor(cred.status)}`}>
                        {cred.status === "pending_review" ? "Pending" : cred.status}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteCredential(cred.id)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Add Credential Modal */}
            <Dialog open={showAddCredential} onOpenChange={setShowAddCredential}>
              <DialogContent className="bg-slate-900 border-slate-700 text-white">
                <DialogHeader>
                  <DialogTitle className="text-white">Add Credential</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-slate-300">Type</Label>
                    <select
                      value={newCredential.credential_type}
                      onChange={(e) => setNewCredential({ ...newCredential, credential_type: e.target.value })}
                      className="w-full mt-1 p-2 bg-slate-800 border border-slate-600 rounded-md text-white"
                    >
                      <option value="certification">Certification</option>
                      <option value="license">License</option>
                      <option value="insurance">Insurance</option>
                      <option value="iso">ISO Certification</option>
                      <option value="fda">FDA Approval</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Name / Description</Label>
                    <Input
                      value={newCredential.credential_name}
                      onChange={(e) => setNewCredential({ ...newCredential, credential_name: e.target.value })}
                      className="bg-slate-800 border-slate-600 text-white"
                      placeholder="e.g., ISO 13485 Medical Devices"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Expiry Date (Optional)</Label>
                    <Input
                      type="date"
                      value={newCredential.expiry_date}
                      onChange={(e) => setNewCredential({ ...newCredential, expiry_date: e.target.value })}
                      className="bg-slate-800 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Notes (Optional)</Label>
                    <Textarea
                      value={newCredential.notes}
                      onChange={(e) => setNewCredential({ ...newCredential, notes: e.target.value })}
                      className="bg-slate-800 border-slate-600 text-white"
                      placeholder="Additional details..."
                      rows={2}
                    />
                  </div>
                  <Button onClick={handleAddCredential} disabled={saving} className="w-full bg-cyan-600 hover:bg-cyan-700">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                    Add Credential
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security" className="space-y-4 mt-4">
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <h3 className="font-medium text-white mb-4 flex items-center gap-2">
                <Lock className="w-4 h-4 text-cyan-400" />
                Change Password
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Current Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      type={showPasswords ? "text" : "password"}
                      value={passwords.current}
                      onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                      className="pl-10 pr-10 bg-slate-800 border-slate-600 text-white"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(!showPasswords)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500"
                    >
                      {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      type={showPasswords ? "text" : "password"}
                      value={passwords.new}
                      onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                      className="pl-10 bg-slate-800 border-slate-600 text-white"
                      placeholder="Minimum 8 characters"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Confirm New Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      type={showPasswords ? "text" : "password"}
                      value={passwords.confirm}
                      onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                      className="pl-10 bg-slate-800 border-slate-600 text-white"
                      placeholder="Re-enter new password"
                    />
                  </div>
                </div>
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword || !passwords.current || !passwords.new || !passwords.confirm}
                  className="w-full bg-cyan-600 hover:bg-cyan-700"
                >
                  {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  Change Password
                </Button>
              </div>
            </div>

            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <div>
                  <p className="font-medium text-amber-400">Account Security</p>
                  <p className="text-sm text-amber-300/70 mt-1">
                    Use a strong password with at least 8 characters. Never share your password with anyone.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
